import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Lottie from 'lottie-react';
import ParentContainor from '../../components/base/ParentContainor';
import { ROUTE_ENUM } from '../../routes/RoutesConstant';
import { logger } from '../../utils/logger';
import { TEXT_LABELS } from '../../constants/TextLabelConstants';
import { IMAGE_CONSTANTS } from '../../constants/ImageConstans';
import { FONTS } from '../../constants/Fonts';
import EmptyRoom from '../../components/EmptyRoom';
import RapHousieListItem from '../../components/RapHousieRoomListItem';
import HousieActionCard from '../../components/HousieActionCard';
import HousieBackButton from '../../components/HousieBackButton';
import JoinRoomModal from '../../modals/JoinRoomModal';
import { rapFireJoinRoom, rapFireUserRoom } from '../../network/network_callbacks';
import { getGameHeroLottie } from '../../constants/LottieConstants';
import { getRapFireImageForRoomType, getRoomTypeFromGame } from '../../components/base/HousieRoomTypeCard';

const SelectRapFireGame = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [availableRooms, setAvailableRooms] = useState([]);
  const [nextGame, setNextGame] = useState(null);
  const [joinModalOpen, setJoinModalOpen] = useState(Boolean(location.state?.openJoinModal));
  const [reduceMotion, setReduceMotion] = useState(false);

  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = (event) => setReduceMotion(event.matches);
    setReduceMotion(media.matches);
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    getRapFireUserRoom();
  }, [navigate]);

  useEffect(() => {
    if (location.state?.openJoinModal) {
      setJoinModalOpen(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.pathname, location.state, navigate]);

  useEffect(() => {
    logger.log('Available Rooms Updated: ', availableRooms);
  }, [availableRooms]);

  const getRapFireUserRoom = async () => {
    try {
      const response = await rapFireUserRoom();
      const responseData = response?.data?.data;
      const nextGameData = responseData?.nextGame || {};
      const room = Array.isArray(responseData)
        ? responseData
        : responseData?.room || responseData?.rooms || [];
      setNextGame(nextGameData || null);
      setAvailableRooms(room || []);
      logger.log('✅ Successfully getRapFireUserRoom : ', response);
      return response;
    } catch (error) {
      console.error('❌ Failed to getRapFireUserRoom : ', error);
    }
  };

  return (
    <ParentContainor
      route={ROUTE_ENUM.SELECT_RAP_FIRE_GAME}
      parentContainorStyle={{ justifyContent: 'flex-start' }}
      isShowHeader={true}
      isBreadcrumbVisible={false}
    >
      <div
        style={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          padding: '10px 0 30px',
          boxSizing: 'border-box',
          minHeight: 'calc(100% - 140px)',
        }}
      >
        <HousieBackButton
          onClick={() => navigate(ROUTE_ENUM.SELECT_GAME.routePath)}
          style={{ alignSelf: 'flex-start', marginBottom: 16 }}
        />

        <div
          style={{
            position: 'relative',
            width: 'min(885px, 100%)',
            margin: '0 auto',
            borderRadius: 36,
            background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.3) 0%, rgba(255, 255, 255, 0.5) 100%)',
            border: '2px solid #FFFFFF',
            boxShadow: '0px 4px 22px 0px rgba(255, 255, 255, 0.7)',
            backdropFilter: 'blur(25px)',
            WebkitBackdropFilter: 'blur(25px)',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 700,
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              padding: '38px 40px 24px',
              textAlign: 'center',
            }}
          >
            <Lottie
              className="rapfire-select-hero-lottie"
              animationData={getGameHeroLottie('rap_fire')}
              loop={!reduceMotion}
              autoplay={!reduceMotion}
              renderer="svg"
              rendererSettings={{ preserveAspectRatio: 'xMidYMid meet' }}
              aria-label="RapFire"
              style={{
                width: 156,
                height: 120,
                marginBottom: 20,
              }}
            />

            <div
              style={{
                fontFamily: FONTS.INTER_EXTRABOLD,
                fontWeight: 800,
                fontSize: 36,
                lineHeight: '44px',
                letterSpacing: 2.5,
                textTransform: 'uppercase',
              }}
            >
              <span
                style={{
                  color: '#003',
                  WebkitTextStroke: '1.2px #8B0000',
                  textShadow: '0 0 1px #8B0000',
                }}
              >
                {TEXT_LABELS.RAP_FIRE_WELCOME_TITLE}
              </span>
              <span
                style={{
                  background: 'linear-gradient(180deg, #FF0000 0%, #FFCB3B 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  color: 'transparent',
                  WebkitTextStroke: '0.6px #8B0000',
                }}
              >
                {` ${TEXT_LABELS.RAP_FIRE_WELCOME_TITLE_BRAND}`}
              </span>
            </div>

            <div
              style={{
                marginTop: 12,
                fontFamily: FONTS.INTER_BOLD,
                fontWeight: 700,
                fontSize: 20,
                lineHeight: '32px',
                letterSpacing: 0.8,
                color: '#003',
              }}
            >
              {TEXT_LABELS.RAP_FIRE_ROOM_TITLE}
            </div>
          </div>

          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'center',
              gap: 24,
              flexWrap: 'wrap',
              padding: '8px 40px 28px',
            }}
          >
            <HousieActionCard
              title={TEXT_LABELS.RAP_FIRE_CREATE_ROOM_TITLE}
              description={TEXT_LABELS.RAP_FIRE_CREATE_ROOM_SUBTITLE}
              image={IMAGE_CONSTANTS.V2_RAP_FIRE_CREATE_ROOM}
              variant="create"
              onClick={() => {
                logger.log('Create Room clicked');
                navigate(ROUTE_ENUM.RAP_FIRE_ROOM_TYPE.routePath, {
                  state: {
                    prevScreen: ROUTE_ENUM.SELECT_RAP_FIRE_GAME.routePath,
                    nextScreen: ROUTE_ENUM.RAP_FIRE_ROOM_TYPE.routePath,
                    isJoin: false,
                  },
                });
              }}
            />
            <HousieActionCard
              title={TEXT_LABELS.RAP_FIRE_JOIN_ROOM_TITLE}
              description={TEXT_LABELS.RAP_FIRE_JOIN_ROOM_SUBTITLE}
              descriptionLine2={TEXT_LABELS.RAP_FIRE_JOIN_ROOM_SUBTITLE_2}
              image={IMAGE_CONSTANTS.V2_RAP_FIRE_JOIN_ROOM}
              variant="join"
              onClick={() => {
                logger.log('Join Room clicked');
                setJoinModalOpen(true);
              }}
            />
          </div>

          <div
            style={{
              flex: 1,
              minHeight: 280,
              marginTop: 'auto',
              borderRadius: 36,
              background: 'linear-gradient(180deg, rgba(1,9,108,0.55) 0%, rgba(1,9,108,0) 100%)',
              padding: availableRooms?.length > 0 ? '24px 50px 28px' : '40px 50px',
              display: 'flex',
              flexDirection: 'column',
              overflowY: 'auto',
              scrollbarWidth: 'none',
              justifyContent: availableRooms?.length > 0 ? 'flex-start' : 'center',
              alignItems: availableRooms?.length > 0 ? 'stretch' : 'center',
            }}
          >
            {availableRooms?.length > 0 ? (
              availableRooms.map((game, index) => {
                const contestId =
                  game?.contestId || game?.gameId || game?.roomId || game?._id || game?.id || '';
                const roomName = game?.roomName || '';
                const roomTypeLabel =
                  getRoomTypeFromGame(game) || TEXT_LABELS.RAP_FIRE_FAMILY_ROOM_LABEL;
                return (
                  <RapHousieListItem
                    key={contestId || index}
                    roomName={roomName}
                    roomTypeLabel={roomTypeLabel}
                    roomTypeImage={getRapFireImageForRoomType(roomTypeLabel)}
                    onClick={() => {
                      logger.log('View Room clicked for roomName: ', roomName);
                      logger.log('View Room clicked for contestId: ', contestId);
                      navigate(ROUTE_ENUM.RAP_FIRE_ROOM_CODE_SHARE_START_GAME.routePath, {
                        state: {
                          prevScreen: ROUTE_ENUM.SELECT_RAP_FIRE_GAME.routePath,
                          nextScreen: ROUTE_ENUM.RAP_FIRE_ROOM_CODE_SHARE_START_GAME.routePath,
                          isJoin: true,
                          roomName: roomName,
                          roomType: game?.roomType,
                          nextGame: nextGame,
                          availableRooms: availableRooms,
                          index: index,
                          selectedGame: game,
                          createdRoomData: {
                            contestId,
                            gameId: contestId,
                            roomName,
                            roomType: game?.roomType,
                            roomCode: game?.roomCode || game?.code,
                          },
                        },
                      });
                    }}
                  />
                );
              })
            ) : (
              <EmptyRoom />
            )}
          </div>
        </div>
      </div>

      <JoinRoomModal
        open={joinModalOpen}
        onClose={() => setJoinModalOpen(false)}
        joinRoomApi={rapFireJoinRoom}
        onJoined={(joinedRoom) => {
          setJoinModalOpen(false);
          navigate(ROUTE_ENUM.RAP_FIRE_JOIN_ANOTHER_ROOM.routePath, {
            state: {
              prevScreen: ROUTE_ENUM.SELECT_RAP_FIRE_GAME.routePath,
              nextScreen: ROUTE_ENUM.RAP_FIRE_JOIN_ANOTHER_ROOM.routePath,
              isJoin: true,
              nextGame: nextGame,
              availableRooms: availableRooms,
              joinableRoom: joinedRoom,
            },
          });
        }}
      />
    </ParentContainor>
  );
};

export default SelectRapFireGame;
